import React, { useState } from 'react';
import Icon from "components/ui/AppIcon.jsx";
import Button from '../../../components/ui/Button.jsx';

const ComplianceChecklist = () => {
  const [expandedSection, setExpandedSection] = useState('certifications');

  const complianceData = {
    certifications: {
      title: 'Certificaciones Vigentes',
      icon: 'Shield',
      items: [
        {
          id: 'iso17025',
          name: 'ISO/IEC 17025:2017',
          status: 'compliant',
          dueDate: '15/03/2025',
          progress: 100,
          priority: 'high'
        },
        {
          id: 'iso9001',
          name: 'ISO 9001:2015',
          status: 'warning',
          dueDate: '28/11/2024',
          progress: 85,
          priority: 'medium'
        },
        {
          id: 'nist',
          name: 'NIST Traceability',
          status: 'compliant',
          dueDate: '10/06/2025',
          progress: 100,
          priority: 'high'
        }
      ]
    },
    audits: {
      title: 'Auditorías Programadas',
      icon: 'FileSearch',
      items: [
        {
          id: 'internal_q4',
          name: 'Auditoría Interna Q4',
          status: 'pending',
          dueDate: '15/12/2024',
          progress: 45,
          priority: 'high'
        },
        {
          id: 'external_annual',
          name: 'Auditoría Externa Anual',
          status: 'scheduled',
          dueDate: '20/02/2025',
          progress: 20,
          priority: 'medium'
        }
      ]
    },
    documentation: {
      title: 'Documentación Requerida',
      icon: 'FileText',
      items: [
        {
          id: 'procedures',
          name: 'Procedimientos Actualizados',
          status: 'compliant',
          dueDate: '30/09/2024',
          progress: 100,
          priority: 'medium'
        },
        {
          id: 'training_records',
          name: 'Registros de Capacitación',
          status: 'warning',
          dueDate: '31/10/2024',
          progress: 70,
          priority: 'high'
        },
        {
          id: 'calibration_certs',
          name: 'Certificados de Calibración',
          status: 'non_compliant',
          dueDate: '25/09/2024',
          progress: 30,
          priority: 'critical'
        }
      ]
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'text-success bg-success/10 border-success/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'non_compliant': case'pending':
        return 'text-error bg-error/10 border-error/20';
      case 'scheduled':
        return 'text-primary bg-primary/10 border-primary/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'non_compliant': case'pending':
        return 'XCircle';
      case 'scheduled':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-error text-error-foreground';
      case 'high':
        return 'bg-warning text-warning-foreground';
      case 'medium':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Lista de Cumplimiento
          </h3>
          <p className="text-sm text-muted-foreground">
            Estado de certificaciones y auditorías
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw">
          Actualizar
        </Button>
      </div>
      <div className="space-y-4">
        {Object.entries(complianceData)?.map(([key, section]) => (
          <div key={key} className="border border-border rounded-lg">
            <button
              onClick={() => toggleSection(key)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Icon name={section?.icon} size={20} className="text-primary" />
                <span className="font-medium text-foreground">{section?.title}</span>
                <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                  {section?.items?.length}
                </span>
              </div>
              <Icon 
                name={expandedSection === key ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground"
              />
            </button>

            {expandedSection === key && (
              <div className="border-t border-border">
                {section?.items?.map((item) => (
                  <div key={item?.id} className="p-4 border-b border-border last:border-b-0">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Icon 
                            name={getStatusIcon(item?.status)} 
                            size={16} 
                            className={getStatusColor(item?.status)?.split(' ')?.[0]}
                          />
                          <span className="font-medium text-foreground text-sm">
                            {item?.name}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(item?.priority)}`}>
                            {item?.priority}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Vencimiento: {item?.dueDate}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" iconName="ExternalLink">
                        Ver
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Progreso</span>
                        <span className="font-medium text-foreground">{item?.progress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            item?.progress >= 90 ? 'bg-success' :
                            item?.progress >= 70 ? 'bg-warning' : 'bg-error'
                          }`}
                          style={{ width: `${item?.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="default" iconName="FileDown" className="flex-1">
            Generar Reporte
          </Button>
          <Button variant="outline" iconName="Calendar" className="flex-1">
            Programar Auditoría
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceChecklist;
